import { useState, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount } from 'wagmi'
import { useNativeTokenBalance } from '~/state/wallet/hooks'
import {
  useLidoSDK,
  useWithdrawalData,
  useIsValidNetwork,
  useClaimGasFee,
} from '../hooks'
import {
  useApprovalModalStateDispatch,
  useModalState,
  useModalToggle,
} from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { ApprovalProgress } from '~/components/ApprovalModal/ApproveStatusLabel'
import {
  TransactionCallback,
  TransactionCallbackStage,
} from '@lidofinance/lido-ethereum-sdk'
import { formatEther } from 'viem'
import { formatFormAmount, toBigNumber, gt } from '~/utils/numbers'
import { RESET_STAGE_TIME } from '../constants'
import RequestsList from './RequestsList'
import PrimaryButton from '~/components/Button/PrimaryButton'
import OrderProgress from '../components/OrderProgress'
import {
  StyledWithdrawalStep,
  StyledClaimSection,
  StyledStepNumber,
  StyledStepTitle,
} from './styled'
import { useIsDisableTrade } from '~/state/dex/hooks'
import { useGA } from '~/hooks'
import { getIsUserCancel } from '../utils'

const ClaimSection = () => {
  const { t } = useTranslation()
  const { trackView, trackClick } = useGA()
  const { address: account } = useAccount()
  const isWalletConnected = !!account
  const { withdraw } = useLidoSDK()
  const isValidNetwork = useIsValidNetwork()
  const ethBalance = useNativeTokenBalance()
  const formattedEthBalance = ethBalance?.toDecimalsFixed()
  const [selectedRequests, setSelectedRequests] = useState<Set<string>>(
    new Set(),
  )
  const [
    currentStage,
    setCurrentStage,
  ] = useState<TransactionCallbackStage | null>(null)
  const [showOrderProgress, setShowOrderProgress] = useState(false)

  const {
    data: withdrawalData,
    mutate: refreshWithdrawalData,
  } = useWithdrawalData()

  const { progress } = useModalState(ApplicationModal.APPROVAL_STATUS)
  const toggleSubmitParamsModal = useModalToggle(
    ApplicationModal.APPROVAL_STATUS,
    {
      stack: true,
      modalState: {
        title: t('submitParamsModal.title'),
        description1: t('submitParamsModal.description_1'),
        description2: t('submitParamsModal.description_2'),
        showLogo: false,
        progress: ApprovalProgress.APPROVING,
      },
    },
  )
  const updateApprovalState = useApprovalModalStateDispatch()

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const totalClaimableEth = useMemo(() => {
    if (
      !withdrawalData?.sortedClaimableRequests ||
      selectedRequests.size === 0
    ) {
      return BigInt(0)
    }

    return withdrawalData.sortedClaimableRequests
      .filter((request) => selectedRequests.has(request.stringId))
      .reduce((total, request) => total + request.claimableEth, BigInt(0))
  }, [withdrawalData?.sortedClaimableRequests, selectedRequests])

  const selectedClaimableRequests = useMemo(() => {
    if (
      !withdrawalData?.sortedClaimableRequests ||
      selectedRequests.size === 0
    ) {
      return []
    }

    return withdrawalData.sortedClaimableRequests.filter((request) =>
      selectedRequests.has(request.stringId),
    )
  }, [withdrawalData?.sortedClaimableRequests, selectedRequests])

  const { gasFee } = useClaimGasFee(selectedClaimableRequests)

  const formattedClaimableEth = formatFormAmount(formatEther(totalClaimableEth))
  const hasSelectedRequests = selectedRequests.size > 0

  const isInsufficientETHForGas = useMemo(() => {
    if (!hasSelectedRequests || !formattedEthBalance || !gasFee) {
      return false
    }
    const ethBalanceBN = toBigNumber(formattedEthBalance)
    const gasFeeRequired = toBigNumber(gasFee)
    return gt(gasFeeRequired, ethBalanceBN)
  }, [hasSelectedRequests, formattedEthBalance, gasFee])

  const claimValueRef = useRef<string>('0')
  const showOrderProgressRef = useRef(showOrderProgress)
  showOrderProgressRef.current = showOrderProgress

  const callback: TransactionCallback = ({ stage }) => {
    const targetStage = [
      TransactionCallbackStage.RECEIPT,
      TransactionCallbackStage.DONE,
      TransactionCallbackStage.MULTISIG_DONE,
      TransactionCallbackStage.ERROR,
    ]
    if (targetStage.includes(stage)) {
      setCurrentStage(stage)
    }

    switch (stage) {
      case TransactionCallbackStage.RECEIPT:
        trackClick('webtl_stk_claim_submit', {
          symbol: 'ETH',
          amount: claimValueRef.current,
        })
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_SUCCESS,
        })
        setShowOrderProgress(true)
        break
      case TransactionCallbackStage.DONE:
      case TransactionCallbackStage.MULTISIG_DONE:
        // 使用 Safe Wallet 时没有 RECEIPT 阶段，直接从 SIGN 阶段跳到 MULTISIG_DONE 阶段
        if (
          progress !== ApprovalProgress.APPROVE_SUCCESS &&
          !showOrderProgressRef.current
        ) {
          trackClick('webtl_stk_claim_submit', {
            symbol: 'ETH',
            amount: claimValueRef.current,
          })
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_SUCCESS,
          })
          setShowOrderProgress(true)
        }
        trackClick('webtl_stk_claim_confirm', {
          symbol: 'ETH',
          amount: claimValueRef.current,
          is_success: true,
        })
        claimValueRef.current = '0'

        refreshWithdrawalData()
        setTimeout(() => {
          setShowOrderProgress(false)
          setSelectedRequests(new Set())
          setCurrentStage(null)
        }, RESET_STAGE_TIME)
        break
      default:
        break
    }
  }

  const handleClaim = async () => {
    if (!withdrawalData?.sortedClaimableRequests || !hasSelectedRequests) {
      return
    }
    setCurrentStage(TransactionCallbackStage.SIGN)
    toggleSubmitParamsModal()

    const requestsIds = selectedClaimableRequests.map((r) => r.id)
    const hints = selectedClaimableRequests.map((r) => r.hint)
    claimValueRef.current = formatEther(totalClaimableEth)

    try {
      await withdraw.claim.claimRequests({
        requestsIds,
        hints,
        callback,
      })
    } catch (error) {
      const isUserCancel = getIsUserCancel(error)
      if (isUserCancel) {
        trackView('webtl_stk_claim_confirm_cancel')
      } else {
        trackClick('webtl_stk_claim_confirm', {
          symbol: 'ETH',
          amount: claimValueRef.current,
          is_success: false,
          message: error.message,
        })
      }
      claimValueRef.current = '0'

      updateApprovalState({
        progress: ApprovalProgress.APPROVE_REJECTED,
      })
      setTimeout(() => {
        setShowOrderProgress(false)
        setSelectedRequests(new Set())
        setCurrentStage(null)
      }, RESET_STAGE_TIME)
    }
  }

  const handleClick = () => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    } else {
      handleClaim()
    }
  }

  const buttonText = useMemo(() => {
    if (isInsufficientETHForGas) {
      return t('preview_$symbol_insufficient_balance', { symbol: 'ETH' })
    }
    return hasSelectedRequests
      ? `${t('claim')} ${formattedClaimableEth} ETH`
      : t('claim')
  }, [hasSelectedRequests, formattedClaimableEth, isInsufficientETHForGas, t])

  const buttonDisabled = useMemo(() => {
    return (
      isWalletConnected &&
      isValidNetwork &&
      (!hasSelectedRequests ||
        isInsufficientETHForGas ||
        currentStage === TransactionCallbackStage.SIGN)
    )
  }, [
    isWalletConnected,
    isValidNetwork,
    hasSelectedRequests,
    isInsufficientETHForGas,
    currentStage,
  ])

  return (
    <div style={{ marginTop: 32 }}>
      <StyledWithdrawalStep>
        <StyledStepNumber>2</StyledStepNumber>
        <StyledStepTitle>{t('claim_eth')}</StyledStepTitle>
      </StyledWithdrawalStep>
      <StyledClaimSection>
        <RequestsList
          selectedRequests={selectedRequests}
          setSelectedRequests={setSelectedRequests}
        />
        {showOrderProgress && <OrderProgress stage={currentStage!} />}
        {!showOrderProgress && (
          <PrimaryButton
            text={buttonText}
            disabled={buttonDisabled}
            loading={currentStage === TransactionCallbackStage.SIGN}
            handleClick={handleClick}
          />
        )}
      </StyledClaimSection>
    </div>
  )
}

export default ClaimSection
